import React, { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { ReactComponent as ArrowRightSvg } from '@assets/icons/arrow-right-without-line.svg';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as ArrowCheckSvg } from '@assets/icons/check-task.svg';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';
import { useGetApprovalWorkflow } from '@hooks/queries/approval';
import { ApprovalWorkflowItemDto, AssetItemDto, AWAssetDto } from '@api/Api';
import { useTypedSelector } from '@hooks';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { sortedAWListAssetsToReviewInBatch } from '@components/Modals/ApprovalModal/utils';
import { useShareReview } from '@components/ShareReviewProvider';
import { useApprovalPassword } from '@context/ApprovalPasswordContext';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { nonNull } from '@helpers/non-null';
import { setAsset, setAssetVariant } from '@redux/actions/mediaViewerAction';

import './AssetsSlider.less';

type AssetsSliderItemProps = {
  asset: AWAssetDto | AssetItemDto;
  isActive: boolean;
  approvalWorkflow?: ApprovalWorkflowItemDto;
  onClick: () => void;
};

function isApprovalAsset(
  asset: AWAssetDto | AssetItemDto
): asset is AWAssetDto {
  return 'decision' in asset;
}

function AssetsSliderItem({
  asset,
  isActive,
  approvalWorkflow,
  onClick
}: AssetsSliderItemProps) {
  const lockDecisions = !!approvalWorkflow?.lockDecisions;
  const decision = isApprovalAsset(asset) ? asset.decision : null;
  const accessDisabled = isApprovalAsset(asset) ? asset.accessDisabled : false;

  const disableReason =
    lockDecisions && !!decision ? (
      <>
        Your decision is locked.
        <br />
        Contact the approval manager
        <br />
        if you need to change it.
      </>
    ) : accessDisabled ? (
      <>
        Your access to this media is restricted.
        <br />
        Contact the approval manager
        <br />
        if you need to request access.
      </>
    ) : null;

  const showLock = !!disableReason;
  const grayOut = !!disableReason || !!decision;

  const previewUrl = isApprovalAsset(asset)
    ? asset.previewUrl
    : asset.asset?.versions[0]?.metadata.preview?.url;
  const assetType = isApprovalAsset(asset)
    ? asset.type
    : asset.asset?.versions[0]?.metadata.type;

  const preparePreview = useMemo(() => {
    const res = assetPreviewThumbnails({
      url: previewUrl,
      status: 'ready',
      type: assetType || 'unknown',
      format: 'img'
    });

    if (typeof res === 'string')
      return <img draggable={false} src={res || ''} alt="" />;
    return res;
  }, [previewUrl, assetType]);

  const name = isApprovalAsset(asset)
    ? asset.name
    : asset.asset?.versions[0]?.name;
  const extension = isApprovalAsset(asset)
    ? asset.extension
    : asset.asset?.versions[0]?.extension;
  const versionNumber = isApprovalAsset(asset)
    ? asset.versionsCount
    : asset.asset?.versions[0]?.versionNumber;

  return (
    <Tooltip
      overlayClassName="assets-slider_tooltip"
      title={
        <>
          {!disableReason && (
            <div className="assets-slider_tooltip-head">
              <div className="assets-slider_tooltip-name">
                {name}
                <span className="assets-slider_tooltip-extension">
                  .{extension}
                </span>
              </div>
              <div className="assets-slider_tooltip-version">
                V{versionNumber}
              </div>
            </div>
          )}
          {disableReason && (
            <div className="assets-slider_tooltip-body">{disableReason}</div>
          )}
        </>
      }
    >
      <div
        className={classNames('assets-slider_item', {
          'assets-slider_item--active': isActive,
          'assets-slider_item--disabled': grayOut,
          'assets-slider_item--blur': accessDisabled
        })}
        onClick={accessDisabled ? undefined : onClick}
        style={{ cursor: accessDisabled ? 'not-allowed' : undefined }}
      >
        {showLock && (
          <div className="assets-slider_item-lock-icon">
            <LockSvg />
          </div>
        )}
        {preparePreview}
        {!!decision && (
          <div
            className={classNames([
              'assets-slider_item-icon',
              `b-color-${decision}`
            ])}
          >
            <ArrowCheckSvg />
          </div>
        )}
      </div>
    </Tooltip>
  );
}

export default function AssetsSlider() {
  const location = useLocation();
  const navigate = useNavigate();
  const shareReview = useShareReview(true);
  const { password } = useApprovalPassword();
  const dispatch = useTypedDispatch();
  const isInternalMediaViewer = location.pathname.includes('media-viewer');
  const selectedAsset = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.assets
  );
  const [searchParams] = useSearchParams();
  const awId =
    searchParams.get('id') ?? selectedAsset?.asset?.approvalWorkflow?.id;
  const selectedAssetId = selectedAsset?.id;
  const contentRef = useRef<HTMLDivElement>(null);
  const { data: resApprovalWorkflow } = useGetApprovalWorkflow({
    params: {
      id: awId || '',
      password
    }
  });
  const aw = resApprovalWorkflow?.approvalWorkflow;

  const sortedAssets = useMemo(() => {
    if (shareReview?.content?.assets) return shareReview.content.assets;
    if (!aw?.assets) return [];
    if (aw.isPaused) return [];
    return sortedAWListAssetsToReviewInBatch(aw.assets);
  }, [shareReview?.content?.assets, aw?.isPaused, aw?.assets]);

  const count = sortedAssets?.length || 0;

  const onChangeAsset = (nextAsset: AWAssetDto | AssetItemDto) => {
    if (!nextAsset) return;
    if (isApprovalAsset(nextAsset)) {
      if (nextAsset.accessDisabled) return;
      if (isInternalMediaViewer && nextAsset.hasAccess) {
        navigate(`/media-viewer/${nextAsset.id}/${nextAsset.versionId}`, {
          replace: true
        });
        return;
      }
      if (isInternalMediaViewer && !nextAsset.hasAccess) {
        navigate(`/approval?id=${awId}&assetId=${nextAsset.id}`, {
          replace: true
        });
        return;
      }
      searchParams.set('assetId', nextAsset.id);
      searchParams.delete('versionId');
      navigate({ search: searchParams.toString() }, { replace: true });
    } else {
      dispatch(setAsset(nextAsset));
      dispatch(setAssetVariant(nonNull(nextAsset.asset).versions[0].id));
    }
  };

  const onPrev = () => {
    const assets = [...sortedAssets].reverse();
    const currentIndex = assets.findIndex(
      (item) => item.id === selectedAssetId
    );
    let prevAsset = assets.find(
      (x, i) => i > currentIndex && (!isApprovalAsset(x) || !x.accessDisabled)
    );
    if (!prevAsset) {
      prevAsset = assets.find(
        (x, i) => i < currentIndex && (!isApprovalAsset(x) || !x.accessDisabled)
      );
    }
    if (prevAsset) onChangeAsset(prevAsset);
  };

  const onNext = () => {
    const currentIndex = sortedAssets.findIndex(
      (item) => item.id === selectedAssetId
    );
    let nextAsset = sortedAssets.find(
      (x, i) => i > currentIndex && (!isApprovalAsset(x) || !x.accessDisabled)
    );
    if (!nextAsset) {
      nextAsset = sortedAssets.find(
        (x, i) => i < currentIndex && (!isApprovalAsset(x) || !x.accessDisabled)
      );
    }
    if (nextAsset) onChangeAsset(nextAsset);
  };

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    if (!sortedAssets.length) return;
    const currentIndex = sortedAssets.findIndex(
      (item) => item.id === selectedAssetId
    );
    const itemElement = content.children[currentIndex];
    if (itemElement) {
      itemElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start'
      });
    }
  }, [sortedAssets, selectedAssetId]);

  if (count <= 1) return null;

  return (
    <div className="assets-slider">
      <button
        className="assets-slider_btn assets-slider_btn-prev"
        type="button"
        onClick={onPrev}
      >
        <ArrowLeftSvg />
      </button>
      <div ref={contentRef} className="assets-slider_content">
        {sortedAssets.map((item) => {
          const isActive = selectedAssetId === item.id;
          return (
            <AssetsSliderItem
              asset={item}
              approvalWorkflow={aw}
              isActive={isActive}
              onClick={() => {
                if (isActive) return;
                onChangeAsset(item);
              }}
              key={item.id}
            />
          );
        })}
      </div>
      <button
        className="assets-slider_btn assets-slider_btn-next"
        type="button"
        onClick={onNext}
      >
        <ArrowRightSvg />
      </button>
    </div>
  );
}
